import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TopBar } from "../components/TopBar";
import { OrgForm } from "../components/OrgForm";
import { Card, ErrorText, btnPrimary, btnSecondary } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { isInContract, type Org, type OrgInput } from "../lib/types";

export function OpsPage() {
  const { t } = useTranslation(["common", "admin"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);

  const orgs = useQuery({
    queryKey: ["ops-orgs"],
    queryFn: () => api<Org[]>("/api/system-admin/orgs"),
  });

  const create = useMutation({
    mutationFn: (v: OrgInput) => api<Org>("/api/system-admin/orgs", { method: "POST", body: v }),
    onSuccess: async () => {
      setShowForm(false);
      setFormKey((k) => k + 1);
      setNotice(t("admin:ops_created"));
      await queryClient.invalidateQueries({ queryKey: ["ops-orgs"] });
    },
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar subtitle={t("common:role_system_admin")} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-primary">{t("admin:ops_title")}</h1>
          <button
            data-testid="ops-org-new"
            type="button"
            onClick={() => { setShowForm(true); setNotice(null); create.reset(); }}
            className={btnPrimary}
          >
            {t("admin:ops_new")}
          </button>
        </div>

        {notice && <p className="mt-4 text-sm text-green-700">{notice}</p>}

        {showForm && (
          <Card className="mt-6 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-primary">{t("admin:ops_new_title")}</h2>
              <button type="button" onClick={() => setShowForm(false)} className={btnSecondary}>{t("common:cancel")}</button>
            </div>
            <OrgForm
              key={formKey}
              submitting={create.isPending}
              submitLabel={t("admin:ops_new")}
              onSubmit={(v) => create.mutate(v)}
            />
            {create.error && (
              <div className="mt-4">
                <ErrorText>{create.error instanceof ApiError ? create.error.message : t("common:error_generic")}</ErrorText>
              </div>
            )}
          </Card>
        )}

        <Card className="mt-6 overflow-x-auto">
          {orgs.isLoading && <p className="text-muted p-6">{t("common:loading")}</p>}
          {orgs.error && <div className="p-6"><ErrorText>{t("common:error_generic")}</ErrorText></div>}
          {orgs.data && orgs.data.length === 0 && <p className="text-muted p-6">{t("admin:ops_no_orgs")}</p>}
          {orgs.data && orgs.data.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs text-muted">
                <tr>
                  <th className="text-left px-4 py-3">{t("admin:org_name")}</th>
                  <th className="text-left px-4 py-3">{t("admin:org_type")}</th>
                  <th className="text-left px-4 py-3">{t("admin:org_status")}</th>
                  <th className="text-left px-4 py-3">{t("admin:org_period")}</th>
                  <th className="text-right px-4 py-3">{t("admin:org_learners")}</th>
                </tr>
              </thead>
              <tbody>
                {orgs.data.map((o) => {
                  const go = () => navigate(`/ops/orgs/${o.id}`);
                  const inContract = isInContract(o);
                  return (
                    <tr
                      key={o.id}
                      data-testid="org-row"
                      role="link"
                      tabIndex={0}
                      onClick={go}
                      onKeyDown={(e) => { if (e.key === "Enter") go(); }}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-4 py-3 font-bold text-primary">{o.name}</td>
                      <td className="px-4 py-3 text-muted">{t(`admin:type_${o.type}`, { defaultValue: o.type })}</td>
                      <td className="px-4 py-3">
                        {o.status === "suspended" ? (
                          <span className="text-accent">{t("common:status_suspended")}</span>
                        ) : inContract ? (
                          <span className="text-green-700">{t("common:status_active")}</span>
                        ) : (
                          <span className="text-accent">{t("admin:org_out_of_contract")}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted text-xs">
                        {o.contract_starts_on || o.contract_ends_on
                          ? `${o.contract_starts_on ?? ""} 〜 ${o.contract_ends_on ?? ""}`
                          : t("admin:org_no_period")}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {o.learner_count ?? 0}
                        <span className="text-xs text-muted"> / {o.learner_cap ?? t("admin:org_no_cap")}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      </main>
    </div>
  );
}
